import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { usePortContext } from '../../../service/MessageChannelContext';
import { ChoiceQuestion } from '../ChoiceQuestion/ChoiceQuestion';
import { AnimationLoader } from '../animationLoader/animationLoader';
import { Card } from '../Card/Card';
import { Navbar } from '../NavBar/NavBar';
import MaleImage from '../../../images/male.png';
import FemaleImage from '../../../images/female.png';
import NormalImage from '../../../images/normal.png';
import RoughTextureImage from '../../../images/rough-texture.png';
import AcneImage from '../../../images/acne.png';
import EnlargedPoresImage from '../../../images/enlarged-pores.png';
import DryImage from '../../../images/dry.png';
import WhiteheadsBlackheadsImage from '../../../images/whitehead-blackhead.png';
import DullSkinImage from '../../../images/dull-skin.png';
import RednessImage from '../../../images/redness.png';
import DarkSpotsPigmentationImage from '../../../images/darkspot-pigmentation.png';
import OilyImage from '../../../images/oily.png';

import LessThan2HoursImage from '../../../images/less-than-2.png';
import MoreThan2HoursImage from '../../../images/greater-than-2.png';

import './skinDetailAnalysis.scss';
import { NumberInputQuestion } from '../NumberInputQuestion/NumberInputQuestion';
import { TextInputQuestion } from '../TextInputQuestion/TextInputQuestion';
import { Button } from '../Button/Button';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { ErrorScreen } from '../errorScreen/errorScreen';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '../DatePickerQuestion/DatePicker';
import { ListPicker } from '../ListPicker/ListPicker';
import { CheckBox } from '../CheckBox/CheckBox';

const citiesInIndonesia = [
  'Ambon',
  'Balikpapan',
  'Banda Aceh',
  'Bandar Lampung',
  'Bandung',
  'Banjar',
  'Banjarbaru',
  'Banjarmasin',
  'Batam',
  'Batu',
  'Baubau',
  'Bekasi',
  'Bengkulu',
  'Bima',
  'Binjai',
  'Bitung',
  'Blitar',
  'Bogor',
  'Bontang',
  'Bukittinggi',
  'Cilegon',
  'Cimahi',
  'Cirebon',
  'Denpasar',
  'Depok',
  'Dumai',
  'Gorontalo',
  'Gunungsitoli',
  'Jakarta Barat',
  'Jakarta Pusat',
  'Jakarta Selatan',
  'Jakarta Timur',
  'Jakarta Utara',
  'Jambi',
  'Jayapura',
  'Kediri',
  'Kendari',
  'Kotamobagu',
  'Kupang',
  'Langsa',
  'Lhokseumawe',
  'Lubuklinggau',
  'Madiun',
  'Magelang',
  'Makassar',
  'Malang',
  'Manado',
  'Mataram',
  'Medan',
  'Metro',
  'Mojokerto',
  'Padang',
  'Padang Panjang',
  'Padang Sidempuan',
  'Pagar Alam',
  'Palangka Raya',
  'Palembang',
  'Palopo',
  'Palu',
  'Pangkalpinang',
  'Parepare',
  'Pariaman',
  'Pasuruan',
  'Payakumbuh',
  'Pekalongan',
  'Pekanbaru',
  'Pematangsiantar',
  'Pontianak',
  'Prabumulih',
  'Probolinggo',
  'Sabang',
  'Salatiga',
  'Samarinda',
  'Sawahlunto',
  'Semarang',
  'Serang',
  'Sibolga',
  'Singkawang',
  'Sorong',
  'Subulussalam',
  'Sukabumi',
  'Sungai Penuh',
  'Surabaya',
  'Surakarta',
  'Tangerang Selatan',
  'Tangerang',
  'Tanjungbalai',
  'Tanjungpinang',
  'Tarakan',
  'Tasikmalaya',
  'Tebing Tinggi',
  'Tegal',
  'Ternate',
  'Tidore Kepulauan',
  'Tomohon',
  'Tual',
  'Yogyakarta',
];

const content = {
  title: {
    en: "Let's get to know you",
    id: "Let's get to know you",
  },
  gender: {
    en: 'Gender',
    id: 'Jenis Kelamin',
  },
  male: {
    en: 'Male',
    id: 'Laki-laki',
  },
  female: {
    en: 'Female',
    id: 'Perempuan',
  },
  skinType: {
    en: 'Skin Type',
    id: 'Jenis Kulit',
  },
  sunExposure: {
    en: 'Sun Exposure',
    id: 'Paparan Sinar Matahari',
  },
  sunExposureDescription: {
    en: 'How often do you go outdoors?',
    id: 'Seberapa sering kamu beraktivitas di luar?',
  },
  normal: {
    en: 'Normal',
    id: 'Normal',
  },
  oily: {
    en: 'Oily',
    id: 'Berminyak',
  },
  dry: {
    en: 'Dry',
    id: 'Kering',
  },
  lessThan2Hours: {
    en: '< 2 hours',
    id: '< 2 jam',
  },
  moreThan2Hours: {
    en: '> 2 hours',
    id: '> 2 jam',
  },
  skinConcerns: {
    en: 'Skin Concerns',
    id: 'Permasalahan Kulit',
  },
  skinConcernsDescription: {
    en: '(Select all that apply)',
    id: '(Pilih semua yang jadi masalahmu)',
  },
  roughTexture: {
    en: 'Rough Texture',
    id: 'Kulit Kasar',
  },
  acnePimples: {
    en: 'Acne & Pimples',
    id: 'Jerawat & Jerawat Batu',
  },
  enlargedPores: { en: 'Enlarged Pores', id: 'Pori-Pori Besar' },
  drySkin: { en: 'Dry Skin', id: 'Kulit Kering' },
  whiteheadsBlackheads: { en: 'Whiteheads & Blackheads', id: 'Whiteheads & Blackheads' },
  dullSkin: { en: 'Dull Skin', id: 'Kulit Kusam' },
  redness: { en: 'Redness', id: 'Kulit Kemerahan' },
  darkSpotsPigmentation: { en: 'Dark Spots & Pigmentation', id: 'Dark Spot & Pigmentation' },
  oilySkin: { en: 'Oily Skin', id: 'Kulit Berminyak' },
  next: { en: 'Next', id: 'Selanjutnya' },
  age: { en: 'Age This Year', id: 'Umur Tahun Ini' },
  getMyResults: { en: 'Get My Results', id: 'Lihat Hasilku' },
  name: { en: 'Name', id: 'Nama' },
  phoneNumber: { en: 'Phone Number (WhatsApp)', id: 'Nomor Telepon (WhatsApp)' },
  email: { en: 'Email', id: 'Email' },
  birthday: { en: 'Birthday', id: 'Tanggal Lahir' },
  location: { en: 'Location', id: 'Lokasi' },
  agreeToPrivacyPolicy: {
    en: 'I agree to processing of my data as described in the Privacy Policy.',
    id: 'Saya setuju dengan pemrosesan data sesuai Kebijakan Privasi.',
  },
  agreeToPromotions: {
    en: 'I agree to receive promotional offers and communications tailored to me according to the Privacy Policy.',
    id: 'Saya setuju menerima penawaran promosi sesuai Kebijakan Privasi.',
  },
  submitAndContinue: { en: 'Submit and continue', id: 'Kirim dan lanjutkan' },
};

const skinConcernImages = {
  roughTexture: RoughTextureImage,
  acnePimples: AcneImage,
  enlargedPores: EnlargedPoresImage,
  drySkin: DryImage,
  whiteheadsBlackheads: WhiteheadsBlackheadsImage,
  dullSkin: DullSkinImage,
  redness: RednessImage,
  darkSpotsPigmentation: DarkSpotsPigmentationImage,
  oilySkin: OilyImage,
};

const skinTypeImages = {
  normal: NormalImage,
  oily: OilyImage,
  dry: DryImage,
};

// TODO: move these schemas into the validation

const SkinDetailAnalysis = () => {
  const {
    analysisId,
    data,
    depthMap,
    lang,
    events: { emitSubmitDataEvent },
    hasAnalysisFailed,
    mode,
  } = usePortContext();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [checkedPrivacy, setCheckedPrivacy] = useState(false);
  const [checkedPromotions, setCheckedPromotions] = useState(false);
  const navigate = useNavigate();

  const isOffline = mode === 'offlineView';

  const validationSchema = Yup.object().shape({
    gender: Yup.string().required('Gender is required'),
    age: Yup.number().typeError('Age must be a number').required('Age is required').min(1, 'Age must be greater than 0'),
    skinType: Yup.string().required('Skin type is required'),
    sunExposure: Yup.string().required('Outdoor activity is required'),
    skinConcerns: Yup.array().min(1, 'At least one concern must be selected'),
    ...(isOffline
      ? {
          name: Yup.string().required('Name is required'),
          phoneNumber: Yup.string().min(10, 'Min 10 characters').required('Phone number is required'),
          email: Yup.string().email('Invalid email').required('Email is required'),
          birthday: Yup.date().required('Birthday is required'),
          location: Yup.string().required('Location is required'),
          promotions: Yup.bool(),
          privacy: Yup.bool().oneOf([true], 'You must agree to the privacy policy'),
        }
      : {}),
  });

  const formik = useFormik({
    initialValues: {
      gender: '',
      age: '',
      skinType: '',
      sunExposure: '',
      skinConcerns: [],
      ...(isOffline
        ? {
            name: '',
            phoneNumber: '',
            email: '',
            birthday: '',
            location: '',
            promotions: false,
            privacy: false,
          }
        : {}),
    },
    validationSchema,
    onSubmit: (values) => {
      emitSubmitDataEvent(analysisId, values);
      setIsLoadingResults(true);
    },
  });

  useEffect(() => {
    if (data && data?.eventName == 'photoUploaded' && depthMap) {
      setIsDisabled(false);
    }
  }, [data, depthMap]);

  return (
    <>
      {!isLoadingResults && (
        <div className="questionnaire">
          <Navbar leftButtonAction={activePage >= 2 ? () => setActivePage(activePage - 1) : undefined} />
          <Card title={content.title[lang]}>
            {activePage === 1 && (
              <>
                <ChoiceQuestion
                  question={content.gender[lang]}
                  options={[
                    {
                      key: 'male',
                      title: content.male[lang],
                      image: MaleImage,
                    },
                    {
                      key: 'female',
                      title: content.female[lang],
                      image: FemaleImage,
                    },
                  ]}
                  selectedOption={formik.values.gender}
                  onOptionSelect={(value) => formik.setFieldValue('gender', value)}
                />
                <NumberInputQuestion
                  question={content.age[lang]}
                  description="Years Old"
                  number={formik.values.age}
                  onNumberChange={(val) => formik.setFieldValue('age', parseInt(val))}
                />
                <ChoiceQuestion
                  question={content.skinType[lang]}
                  options={['normal', 'oily', 'dry'].map((key) => ({
                    key,
                    title: content[key][lang],
                    image: skinTypeImages[key],
                  }))}
                  selectedOption={formik.values.skinType}
                  onOptionSelect={(value) => formik.setFieldValue('skinType', value)}
                />
                <ChoiceQuestion
                  question={content.sunExposure[lang]}
                  description={content.sunExposureDescription[lang]}
                  options={[
                    {
                      key: 'lessThan2Hours',
                      title: content.lessThan2Hours[lang],
                      image: LessThan2HoursImage,
                    },
                    {
                      key: 'moreThan2Hours',
                      title: content.moreThan2Hours[lang],
                      image: MoreThan2HoursImage,
                    },
                  ]}
                  selectedOption={formik.values.sunExposure}
                  onOptionSelect={(value) => formik.setFieldValue('sunExposure', value)}
                />
              </>
            )}
            {activePage === 2 && (
              <ChoiceQuestion
                question={content.skinConcerns[lang]}
                description={content.skinConcernsDescription[lang]}
                options={[
                  'roughTexture',
                  'acnePimples',
                  'enlargedPores',
                  'drySkin',
                  'whiteheadsBlackheads',
                  'dullSkin',
                  'redness',
                  'darkSpotsPigmentation',
                  'oilySkin',
                ].map((key) => {
                  return {
                    key,
                    title: content[key][lang],
                    image: skinConcernImages[key],
                  };
                })}
                selectedOption={formik.values.skinConcerns}
                onOptionSelect={(value) => formik.setFieldValue('skinConcerns', value)}
              />
            )}

            {activePage === 3 && (
              <>
                <TextInputQuestion
                  question={content.name[lang]}
                  text={formik.values.name}
                  onTextChange={(value) => formik.setFieldValue('name', value)}
                />
                <TextInputQuestion
                  question={content.phoneNumber[lang]}
                  text={formik.values.phoneNumber || '08'}
                  onTextChange={(value) => formik.setFieldValue('phoneNumber', value)}
                  errorMessage={formik.touched.phoneNumber && formik.errors.phoneNumber}
                  isNumber
                />
                <TextInputQuestion
                  question={content.email[lang]}
                  text={formik.values.email}
                  onTextChange={(value) => formik.setFieldValue('email', value)}
                  errorMessage={formik.touched.email && formik.errors.email}
                />
                <DatePicker
                  question={content.birthday[lang]}
                  date={formik.values.birthday}
                  onDateChange={(value) => formik.setFieldValue('birthday', value)}
                />
                <ListPicker
                  question={content.location[lang]}
                  selectedOption={formik.values.location}
                  onOptionChange={(value) => formik.setFieldValue('location', value)}
                  options={citiesInIndonesia}
                />
              </>
            )}

            <hr />

            <ProgressBar pageNo={activePage} totalPages={isOffline ? 3 : 2} />

            {activePage === 3 && (
              <>
                <CheckBox
                  text={'I agree to processing of my data as described in the Privacy Policy.'}
                  link={'https://www.paragon-innovation.com/privacy-policy'}
                  isChecked={formik.values.privacy}
                  onChange={(value) => {
                    formik.setFieldValue('privacy', value);
                  }}
                />
                <CheckBox
                  text={'I agree to receive promotional offers and communications tailored to me according to the Privacy Policy.'}
                  link={'https://www.paragon-innovation.com/terms-conditions'}
                  isChecked={formik.values.promotions}
                  onChange={(value) => formik.setFieldValue('promotions', value)}
                />
              </>
            )}

            {activePage === 1 && (
              <Button
                text={content.next[lang]}
                onClick={() => setActivePage(2)}
                isDisabled={!formik.values.gender || !formik.values.age || !formik.values.skinType || !formik.values.sunExposure}
              />
            )}
            {activePage === 2 && (
              <Button
                text={content.getMyResults[lang]}
                onClick={isOffline ? () => setActivePage(3) : formik.handleSubmit}
                isDisabled={!formik.values.skinConcerns?.length || (isDisabled && !isOffline)}
              />
            )}
            {activePage === 3 && (
              <Button
                text={'Submit and continue'}
                onClick={formik.handleSubmit}
                isDisabled={
                  !formik.values.name ||
                  !formik.values.phoneNumber ||
                  !formik.values.phoneNumber.length >= 10 ||
                  !formik.values.email ||
                  !formik.values.birthday ||
                  !formik.values.location ||
                  !formik.values.privacy ||
                  isDisabled
                }
              />
            )}
          </Card>
        </div>
      )}
      {isLoadingResults && <AnimationLoader />}
      {hasAnalysisFailed && (
        <ErrorScreen
          retry={() => {
            navigate('/howTo');
          }}
          message={'An Error occured while getting your skin report.'}
        />
      )}
    </>
  );
};

export default SkinDetailAnalysis;
